/**
 * A pet's record, inside its owner's file.
 *
 * Pets used to be a top-level module, which was the wrong shape: a pet only
 * exists as somebody's pet, and every question an operator has about one ("is
 * this the animal on the booking?", "why was this profile taken down?") starts
 * from the owner. So this lives at /users/:user_id/pets/:pet_id, breadcrumbs
 * back through the customer, and the nav entry is gone.
 *
 * What it shows that nothing showed before: the vaccination schedule with
 * overdue flagged, allergies and conditions, the lifecycle log (created,
 * transferred, surrendered, removed by an admin), vet-written medical records,
 * and the bookings made for this animal — plus the moderation controls to take
 * a profile down and put it back, both of which write an audited reason.
 */
import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  CalendarCheck2,
  Cake,
  CheckCircle2,
  Copy,
  Dna,
  FileText,
  History,
  Images,
  PawPrint,
  ScanLine,
  Stethoscope,
  Syringe,
  User,
  Weight,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import {
  DetailPanel,
  Field,
  FieldList,
  PanelEmpty,
  PanelSkeleton,
} from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { AuthImage, open_file_handler } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { ReasonModal } from '@/features/vendors/components/reason-modal';
import { notify } from '@/components/ui/toaster';
import { ago, date_short, date_time, inr, title_case } from '@/lib/format';
import { use_pet_detail, use_set_pet_active } from '@/features/pets/hooks';
import type { PetDetail } from '@/features/pets/types';
import { cn } from '@/lib/utils';

/**
 * Vaccine names, because several are acronyms.
 *
 * `title_case` turns DHPP into "Dhpp" and FVRCP into "Fvrcp", which reads as a
 * typo to anyone who knows what they are — and the people reading this page are
 * exactly those people.
 */
const VACCINE_LABEL: Record<string, string> = {
  RABIES: 'Rabies',
  DHPP: 'DHPP',
  BORDETELLA: 'Bordetella',
  LEPTOSPIROSIS: 'Leptospirosis',
  CANINE_INFLUENZA: 'Canine influenza',
  LYME: 'Lyme',
  FVRCP: 'FVRCP',
  FELV: 'FeLV',
  OTHER: 'Other',
};

/** Days until a vaccination is due; negative means overdue. */
function days_until(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

const SEVERITY_TONE: Record<string, 'success' | 'warning' | 'destructive' | 'neutral'> = {
  MILD: 'success',
  MODERATE: 'warning',
  SEVERE: 'destructive',
};

const CONDITION_TONE: Record<string, 'warning' | 'destructive' | 'success' | 'neutral'> = {
  ACTIVE: 'destructive',
  CHRONIC: 'warning',
  RESOLVED: 'success',
};

/** Lifecycle types, with the tone and wording an operator reads them by. */
const LIFECYCLE: Record<
  string,
  { label: string; tone: 'neutral' | 'info' | 'warning' | 'destructive' | 'success' }
> = {
  CREATED: { label: 'Profile created', tone: 'neutral' },
  OWNERSHIP_TRANSFERRED: { label: 'Ownership transferred', tone: 'info' },
  MARKED_DECEASED: { label: 'Marked deceased', tone: 'warning' },
  SURRENDERED: { label: 'Surrendered by owner', tone: 'warning' },
  REACTIVATED: { label: 'Restored', tone: 'success' },
  ADMIN_REMOVED: { label: 'Removed by an admin', tone: 'destructive' },
};

export function PetDetailPage() {
  const { user_id = '', pet_id = '' } = useParams<{ user_id: string; pet_id: string }>();
  const { data: pet, isLoading, error, refetch } = use_pet_detail(pet_id);
  const set_active = use_set_pet_active();
  const [modal, set_modal] = useState<'remove' | 'restore' | null>(null);
  const [modal_error, set_modal_error] = useState<string | null>(null);

  // Prefer the URL's owner, but fall back to whatever the API reports — a
  // /pets/:id deep link has no owner segment to read.
  const owner_id = user_id || pet?.owner.id || '';
  const owner_label = pet?.owner.name ?? pet?.owner.phone ?? 'Owner';

  if (error) {
    return (
      <DashboardLayout title="Pet" breadcrumbs={[{ label: 'Not found' }]}>
        <ErrorState
          title="Couldn't load this pet"
          description={error instanceof Error ? error.message : 'The request failed.'}
          action={
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
              {owner_id && (
                <Button size="sm" variant="ghost" asChild>
                  <Link to={`/users/${owner_id}?tab=pets`}>Back to the owner</Link>
                </Button>
              )}
            </div>
          }
        />
      </DashboardLayout>
    );
  }

  const overdue = (pet?.vaccinations ?? []).filter((v) => {
    const d = days_until(v.next_due_on);
    return d !== null && d < 0;
  }).length;

  const active_conditions = (pet?.conditions ?? []).filter(
    (c) => c.status === 'ACTIVE' || c.status === 'CHRONIC',
  ).length;

  const metrics: Metric[] = [
    {
      key: 'bookings',
      label: 'Bookings',
      value: pet?.counts.bookings,
      icon: CalendarCheck2,
      loading: isLoading,
    },
    {
      key: 'vaccinations',
      label: 'Vaccinations',
      value: pet?.counts.vaccinations,
      hint: overdue > 0 ? `${overdue} overdue` : 'All up to date',
      icon: Syringe,
      tone: overdue > 0 ? 'destructive' : 'success',
      loading: isLoading,
    },
    {
      key: 'allergies',
      label: 'Allergies',
      value: pet?.counts.allergies,
      icon: AlertTriangle,
      tone: pet && pet.counts.allergies > 0 ? 'warning' : 'default',
      loading: isLoading,
    },
    {
      key: 'conditions',
      label: 'Conditions',
      value: pet?.counts.conditions,
      hint: active_conditions > 0 ? `${active_conditions} active or chronic` : undefined,
      icon: Stethoscope,
      tone: active_conditions > 0 ? 'warning' : 'default',
      loading: isLoading,
    },
    {
      key: 'records',
      label: 'Vet records',
      value: pet?.counts.medical_records,
      icon: FileText,
      loading: isLoading,
    },
  ];

  function moderate(reason: string) {
    const restoring = modal === 'restore';
    set_active.mutate(
      { id: pet_id, is_active: restoring, reason },
      {
        onSuccess: (res) => {
          set_modal(null);
          if (res.already_in_state) {
            notify.info('Nothing to change', {
              description: `This profile was already ${restoring ? 'visible' : 'hidden'}.`,
            });
            return;
          }
          notify.success(
            restoring ? `${pet?.name ?? 'Pet'} restored` : `${pet?.name ?? 'Pet'} removed`,
            { description: 'The reason is recorded in the lifecycle log below.' },
          );
        },
        onError: (err) => {
          const message = err instanceof Error ? err.message : 'Could not update this pet';
          set_modal_error(message);
          notify.error(err, 'Could not update this pet.');
        },
      },
    );
  }

  return (
    <DashboardLayout
      title={pet?.name ?? 'Pet'}
      subtitle={
        pet
          ? [
              title_case(pet.species),
              pet.breed_name,
              pet.age_label,
              pet.sex !== 'UNKNOWN' ? title_case(pet.sex) : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
      breadcrumbs={[
        { label: owner_label, to: owner_id ? `/users/${owner_id}` : undefined },
        { label: pet?.name ?? 'Pet' },
      ]}
      avatar={<PetPortrait pet={pet} />}
      title_badges={
        pet && (
          <>
            {pet.is_active ? (
              <Badge tone="success" dot>
                Visible
              </Badge>
            ) : (
              <Badge tone="destructive" dot>
                Hidden
              </Badge>
            )}
            {overdue > 0 && (
              <Badge tone="destructive">
                <Syringe className="h-3 w-3" /> {overdue} vaccination
                {overdue === 1 ? '' : 's'} overdue
              </Badge>
            )}
          </>
        )
      }
      meta={
        pet && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-data text-muted-foreground">
            <Link
              to={`/users/${pet.owner.id}`}
              className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
            >
              <User aria-hidden="true" className="h-3.5 w-3.5" />
              {owner_label}
            </Link>
            {pet.owner.phone && <span className="tabular">{pet.owner.phone}</span>}
            {pet.microchip_id && (
              <span className="inline-flex items-center gap-1.5">
                <ScanLine aria-hidden="true" className="h-3.5 w-3.5" />
                <span className="tabular">{pet.microchip_id}</span>
              </span>
            )}
          </div>
        )
      }
      actions={
        pet && (
          <>
            {owner_id && (
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/users/${owner_id}?tab=pets`} data-testid="back-to-owner">
                  <ArrowLeft className="h-4 w-4" /> Owner
                </Link>
              </Button>
            )}
            {pet.microchip_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(pet.microchip_id ?? '');
                  notify.success('Microchip ID copied');
                }}
                data-testid="copy-microchip"
              >
                <Copy className="h-4 w-4" /> Microchip
              </Button>
            )}
            {pet.is_active ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  set_modal_error(null);
                  set_modal('remove');
                }}
                data-testid="pet-remove"
              >
                <Ban className="h-4 w-4" /> Remove profile
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => {
                  set_modal_error(null);
                  set_modal('restore');
                }}
                data-testid="pet-restore"
              >
                <CheckCircle2 className="h-4 w-4" /> Restore profile
              </Button>
            )}
          </>
        )
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      {/*
        A hidden profile needs saying loudly and with its reason: the customer
        cannot see this pet in the app, and the next operator to open the page
        must not have to infer that from a badge.
      */}
      {pet && !pet.is_active && (
        <div
          className="flex items-start gap-2.5 rounded-panel bg-destructive/8 p-3 text-data text-destructive"
          data-testid="pet-hidden-notice"
        >
          <Ban aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">This profile is hidden from the owner.</p>
            <p className="text-destructive/80">
              {(() => {
                const last = pet.lifecycle_events.find(
                  (e) => e.type === 'ADMIN_REMOVED' || e.type === 'SURRENDERED',
                );
                if (last?.type === 'ADMIN_REMOVED') {
                  return `Removed by an admin ${ago(last.occurred_at)}${last.notes ? ` — ${last.notes}` : ''}`;
                }
                return 'The owner deleted this pet from their app.';
              })()}
            </p>
          </div>
        </div>
      )}

      {/*
        Two columns that each own a stack, rather than two separate
        two-column grids. Stacking grids meant the shorter cell in the
        first one (Profile) left a column-wide hole above the second,
        which read as a rendering fault rather than a layout choice.
      */}
      <div className="grid items-start gap-gutter lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-gutter">
          <DetailPanel title="Profile" icon={PawPrint} test_id="panel-pet-profile">
            {isLoading || !pet ? (
              <PanelSkeleton rows={8} />
            ) : (
              <FieldList>
                <Field label="Species" value={title_case(pet.species)} />
                <Field
                  label="Breed"
                  value={pet.breed_name ?? pet.mixed_breed_description}
                  empty="Not recorded"
                />
                <Field
                  label="Age"
                  value={
                    pet.age_label && (
                      <span className="inline-flex items-center gap-1.5">
                        <Cake aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground" />
                        {pet.age_label}
                        {pet.birth_date && (
                          <span className="text-muted-foreground">
                            (born {date_short(pet.birth_date)})
                          </span>
                        )}
                      </span>
                    )
                  }
                  empty="Not recorded"
                />
                <Field
                  label="Sex"
                  value={pet.sex === 'UNKNOWN' ? null : title_case(pet.sex)}
                  empty="Unknown"
                />
                <Field
                  label="Neutered"
                  value={pet.neuter_status === 'UNKNOWN' ? null : title_case(pet.neuter_status)}
                  empty="Unknown"
                />
                <Field
                  label="Weight"
                  value={
                    pet.weight_kg !== null && (
                      <span className="inline-flex items-center gap-1.5 tabular">
                        <Weight aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground" />
                        {pet.weight_kg} kg
                      </span>
                    )
                  }
                  empty="Not recorded"
                />
                <Field label="Markings" value={pet.color_markings} empty="Not recorded" />
                <Field label="Microchip" value={pet.microchip_id} mono empty="Not chipped" />
                <Field label="Owner notes" value={pet.notes} empty="None" />
                <Field label="Added" value={date_time(pet.created_at)} />
              </FieldList>
            )}
          </DetailPanel>

          <DetailPanel
            title="Vet records"
            icon={FileText}
            description={pet ? `${pet.counts.medical_records} in total` : undefined}
            test_id="panel-medical-records"
          >
            {isLoading || !pet ? (
              <PanelSkeleton rows={3} />
            ) : pet.medical_records.length === 0 ? (
              <PanelEmpty icon={FileText}>No vet has written a record for this pet yet.</PanelEmpty>
            ) : (
              <ul className="flex flex-col divide-y divide-border/60">
                {pet.medical_records.map((r) => (
                  <li key={r.id} className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={r.status === 'FINALIZED' ? 'success' : 'warning'} size="sm">
                        {title_case(r.status)}
                      </Badge>
                      <span className="text-data font-semibold">
                        {r.vendor_business_name ?? title_case(r.module_type)}
                      </span>
                      <span className="text-micro text-muted-foreground">{ago(r.created_at)}</span>
                      <Link
                        to={`/bookings/${r.booking_id}`}
                        className="ml-auto text-micro font-semibold text-primary hover:underline"
                      >
                        Booking
                      </Link>
                    </div>
                    {r.chief_complaint && (
                      <p className="text-micro text-muted-foreground">“{r.chief_complaint}”</p>
                    )}
                    <p className="text-data">{r.diagnosis}</p>
                    {r.follow_up_on && (
                      <p className="text-micro text-warning">
                        Follow-up due {date_short(r.follow_up_on)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </DetailPanel>
        </div>

        <div className="flex min-w-0 flex-col gap-gutter">
          <DetailPanel
            title="Vaccinations"
            icon={Syringe}
            description={pet ? `${pet.vaccinations.length} recorded` : undefined}
            test_id="panel-vaccinations"
          >
            {isLoading || !pet ? (
              <PanelSkeleton rows={3} />
            ) : pet.vaccinations.length === 0 ? (
              <PanelEmpty icon={Syringe}>
                No vaccinations recorded. Boarding and daycare vendors usually require proof.
              </PanelEmpty>
            ) : (
              <ul className="flex flex-col divide-y divide-border/60">
                {pet.vaccinations.map((v) => {
                  const d = days_until(v.next_due_on);
                  return (
                    <li
                      key={v.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-2 first:pt-0 last:pb-0"
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="text-data font-semibold">
                          {VACCINE_LABEL[v.type] ?? title_case(v.type)}
                        </span>
                        <span className="text-micro text-muted-foreground">
                          Given {date_short(v.administered_on)}
                          {v.administered_by ? ` · ${v.administered_by}` : ''}
                        </span>
                      </span>
                      {d === null ? (
                        <Badge tone="neutral" size="sm">
                          No due date
                        </Badge>
                      ) : d < 0 ? (
                        <Badge tone="destructive" size="sm">
                          Overdue by {Math.abs(d)}d
                        </Badge>
                      ) : d <= 30 ? (
                        <Badge tone="warning" size="sm">
                          Due in {d}d
                        </Badge>
                      ) : (
                        <Badge tone="success" size="sm">
                          Due {date_short(v.next_due_on)}
                        </Badge>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </DetailPanel>

          <DetailPanel title="Allergies" icon={AlertTriangle} test_id="panel-allergies">
            {isLoading || !pet ? (
              <PanelSkeleton rows={2} />
            ) : pet.allergies.length === 0 ? (
              <PanelEmpty icon={AlertTriangle}>None recorded.</PanelEmpty>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {pet.allergies.map((a) => (
                  <Badge key={a.id} tone={SEVERITY_TONE[a.severity] ?? 'neutral'} dot>
                    {a.allergen} · {title_case(a.severity)}
                  </Badge>
                ))}
              </div>
            )}
          </DetailPanel>

          <DetailPanel title="Medical conditions" icon={Stethoscope} test_id="panel-conditions">
            {isLoading || !pet ? (
              <PanelSkeleton rows={2} />
            ) : pet.conditions.length === 0 ? (
              <PanelEmpty icon={Stethoscope}>None recorded.</PanelEmpty>
            ) : (
              <ul className="flex flex-col divide-y divide-border/60">
                {pet.conditions.map((c) => (
                  <li
                    key={c.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2 first:pt-0 last:pb-0"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="text-data font-semibold">{c.condition}</span>
                      {c.diagnosed_on && (
                        <span className="text-micro text-muted-foreground">
                          Diagnosed {date_short(c.diagnosed_on)}
                        </span>
                      )}
                    </span>
                    <Badge tone={CONDITION_TONE[c.status] ?? 'neutral'} size="sm">
                      {title_case(c.status)}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </DetailPanel>
          <DetailPanel
            title="Photos"
            icon={Images}
            description={pet ? `${pet.counts.photos} uploaded` : undefined}
            test_id="panel-pet-photos"
          >
            {isLoading || !pet ? (
              <PanelSkeleton rows={2} />
            ) : pet.photos.length === 0 ? (
              <PanelEmpty icon={Images}>No photos uploaded.</PanelEmpty>
            ) : (
              <div className="flex flex-wrap gap-2">
                {pet.photos.map((ph) => (
                  <button
                    key={ph.id}
                    type="button"
                    // Photos need the admin bearer token, so opening one goes
                    // through the API client rather than a plain link.
                    onClick={open_file_handler(ph.full_path, (msg) => notify.error(msg))}
                    className="group relative overflow-hidden rounded-panel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    title="Open full size"
                  >
                    <AuthImage
                      path={ph.thumb_path}
                      alt={`${pet.name} photo`}
                      className="h-20 w-20 object-cover transition-transform group-hover:scale-105"
                    />
                    {ph.is_primary && (
                      <span className="absolute bottom-0 left-0 right-0 bg-foreground/70 py-0.5 text-center text-micro font-semibold text-background">
                        Primary
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </DetailPanel>

          <DetailPanel
            title="Lifecycle"
            icon={History}
            description="Ownership and visibility history"
            test_id="panel-lifecycle"
          >
            {isLoading || !pet ? (
              <PanelSkeleton rows={3} />
            ) : pet.lifecycle_events.length === 0 ? (
              <PanelEmpty icon={History}>No lifecycle events recorded.</PanelEmpty>
            ) : (
              <ol className="flex flex-col gap-0">
                {pet.lifecycle_events.map((e, i) => {
                  const meta = LIFECYCLE[e.type] ?? { label: title_case(e.type), tone: 'neutral' };
                  const last = i === pet.lifecycle_events.length - 1;
                  return (
                    <li key={e.id} className="flex gap-2.5">
                      {/* A rail rather than a bordered list: the order is the
                          point, and a timeline reads as ordered. */}
                      <span className="flex flex-col items-center pt-1.5">
                        <span
                          className={cn(
                            'h-2 w-2 shrink-0 rounded-full',
                            meta.tone === 'destructive'
                              ? 'bg-destructive'
                              : meta.tone === 'warning'
                                ? 'bg-warning'
                                : meta.tone === 'success'
                                  ? 'bg-success'
                                  : meta.tone === 'info'
                                    ? 'bg-info'
                                    : 'bg-muted-foreground/50',
                          )}
                        />
                        {!last && <span className="w-px flex-1 bg-border" />}
                      </span>
                      <span className={cn('flex min-w-0 flex-col', last ? 'pb-0' : 'pb-3')}>
                        <span className="text-data font-semibold">{meta.label}</span>
                        <span className="text-micro text-muted-foreground">
                          {date_time(e.occurred_at)}
                        </span>
                        {e.notes && (
                          <span className="text-data text-muted-foreground">{e.notes}</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </DetailPanel>
        </div>
      </div>

      <DetailPanel
        title="Bookings for this pet"
        icon={CalendarCheck2}
        description={pet ? `${pet.counts.bookings} in total` : undefined}
        test_id="panel-pet-bookings"
        body_class="p-0"
      >
        {isLoading || !pet ? (
          <div className="p-3.5">
            <PanelSkeleton rows={4} />
          </div>
        ) : pet.bookings.length === 0 ? (
          <PanelEmpty icon={CalendarCheck2}>No service has been booked for this pet.</PanelEmpty>
        ) : (
          <div className="thin-scroll overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  {['When', 'Service', 'Vendor', 'Price', 'Status'].map((h, i) => (
                    <th
                      key={h}
                      scope="col"
                      className={cn(
                        'border-b border-border bg-muted/50 px-3 py-1.5 text-micro font-bold uppercase tracking-wide text-muted-foreground',
                        i === 3 ? 'text-right' : 'text-left',
                      )}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pet.bookings.map((b) => (
                  <tr key={b.id} className="[&:nth-child(even)]:bg-muted/20">
                    <td className="border-b border-border/50 px-3 py-2 text-data">
                      <Link
                        to={`/bookings/${b.id}`}
                        className="whitespace-nowrap font-medium underline-offset-2 hover:text-primary hover:underline"
                      >
                        {date_time(b.slot_start)}
                      </Link>
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-data">
                      {title_case(b.module_type)}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-data">
                      {b.vendor_business_name ?? '—'}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-right text-data tabular">
                      {inr(b.price_inr)}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-data">
                      <Badge
                        tone={
                          b.status === 'COMPLETED'
                            ? 'success'
                            : b.status === 'CANCELLED' || b.status === 'NO_SHOW'
                              ? 'destructive'
                              : 'info'
                        }
                        dot
                      >
                        {title_case(b.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetailPanel>

      <ReasonModal
        open={modal !== null}
        title={modal === 'restore' ? 'Restore this pet profile?' : 'Remove this pet profile?'}
        description={
          modal === 'restore'
            ? `${pet?.name ?? 'This pet'} will be visible to the owner again.`
            : `${pet?.name ?? 'This pet'} will disappear from the owner's app, exactly as if they had deleted it. Existing bookings are unaffected.`
        }
        confirm_label={modal === 'restore' ? 'Restore profile' : 'Remove profile'}
        variant={modal === 'restore' ? 'default' : 'destructive'}
        loading={set_active.isPending}
        error={modal_error}
        on_cancel={() => set_modal(null)}
        on_confirm={moderate}
      />
    </DashboardLayout>
  );
}

/** The pet's primary photo, or a species glyph, sized for the page header. */
function PetPortrait({ pet }: { pet: PetDetail | undefined }) {
  const primary = pet?.photos.find((p) => p.is_primary) ?? pet?.photos[0];
  if (primary) {
    return (
      <AuthImage
        path={primary.thumb_path}
        alt=""
        className="h-14 w-14 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
      <Dna aria-hidden="true" className="h-6 w-6" />
    </span>
  );
}

/**
 * Compatibility route for the old top-level `/pets/:id` URL.
 *
 * Pets are no longer a module of their own, so the canonical address is
 * owner-scoped. Links that predate that — a bookmarked page, a palette result,
 * a pasted URL in a ticket — resolve the owner and land on the real page rather
 * than 404ing. Replaces the history entry so Back doesn't bounce through here.
 */
export function PetDetailRedirect() {
  const { id = '' } = useParams<{ id: string }>();
  const { data: pet, isLoading, error } = use_pet_detail(id);

  if (isLoading) {
    return (
      <DashboardLayout title="Pet">
        <DetailPanel title="Opening pet" icon={PawPrint}>
          <PanelSkeleton rows={3} />
        </DetailPanel>
      </DashboardLayout>
    );
  }
  if (error || !pet) {
    return (
      <DashboardLayout title="Pet" breadcrumbs={[{ label: 'Not found' }]}>
        <ErrorState
          title="That pet no longer exists"
          description="It may have been deleted. Search for the owner instead."
          action={
            <Button size="sm" variant="outline" asChild>
              <Link to="/users">Go to customers</Link>
            </Button>
          }
        />
      </DashboardLayout>
    );
  }
  return <Navigate to={`/users/${pet.owner.id}/pets/${pet.id}`} replace />;
}
